import {
  AfriexAdminPermission,
  AfriexCurrencyCode,
} from '@afriex/core/domain/enums';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {EUFlag} from '@/constants/countries';
import type {ResolverCapabilityMatrix} from '@/services/processorService';
import type {AfriexCountry, AfriexCountryCode, AfriexCurrency} from '@/types';
import {mapCountryCodeToFlags} from '@/utils/countries';

import {RESOLVER_FAMILIES} from './types';

// Th/Td carry no horizontal padding of their own; each table sets it.
const FIRST_CELL = 'pl-6 pr-4';
const CELL = 'px-4';

export interface ResolverTableProps {
  /** One row per currency, including currencies with no resolver configured. */
  items: AfriexCurrency[];
  countryList: AfriexCountry[];
  /** Representative active country per currency code, for the flag only. */
  countryCodeByCurrency: Record<string, AfriexCountryCode>;
  /** Which providers can serve each currency, for the not-eligible marker. */
  matrix: ResolverCapabilityMatrix;
  showResolverUpdateForm: (currency: AfriexCurrency) => void;
}

/**
 * Flat, unlike the Corridors table.
 *
 * A currency has four resolvers and each is a single value, so they fit as
 * columns. Corridors needs an expandable row because it carries a per-channel
 * processor plus a fallback search order under two transaction types.
 *
 * A configured value the capability matrix does not offer for that currency is
 * marked in place. Finding those is the point of having every currency on one
 * screen: the dropdowns stop new ones being created, but a value stored before
 * the matrix existed is only visible if the table says so.
 */
const ResolverTable = ({
  items,
  countryList,
  countryCodeByCurrency,
  matrix,
  showResolverUpdateForm,
}: ResolverTableProps) => {
  const flagMap = mapCountryCodeToFlags(countryList);

  const isEligible = (
    family: (typeof RESOLVER_FAMILIES)[number]['key'],
    code: string | undefined,
    processor: string,
  ) =>
    (
      (matrix?.[family]?.[code as AfriexCurrencyCode] ?? []) as string[]
    ).includes(processor);

  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className={FIRST_CELL}>Currency</Th>
          {RESOLVER_FAMILIES.map(({key, label}) => (
            <Th key={key} className={CELL}>
              {label}
            </Th>
          ))}
          <Th className={`w-20 ${CELL}`}>&nbsp;</Th>
        </Tr>
      </Thead>

      <TBody>
        {items.map((item) => {
          const flag =
            item?.code === AfriexCurrencyCode.EUR
              ? EUFlag
              : flagMap[countryCodeByCurrency?.[item?.code as string]];

          return (
            <Tr key={item?.code ?? item?.id}>
              <Td className={`${FIRST_CELL} font-medium`}>
                <span className="mr-2">{flag}</span>
                {item?.code}
              </Td>

              {RESOLVER_FAMILIES.map(({key, label}) => {
                const configured = item?.params?.resolvers?.[key];
                const isStale =
                  Boolean(configured) &&
                  !isEligible(key, item?.code, configured as string);

                return (
                  <Td
                    key={key}
                    className={CELL}
                    data-testid={`resolver-${item?.code}-${key}`}
                  >
                    {configured ? (
                      <span
                        className={
                          isStale ? 'text-amber-600 dark:text-amber-500' : ''
                        }
                        title={
                          isStale
                            ? `${configured} is not available for ${label.toLowerCase()} in ${item?.code}`
                            : undefined
                        }
                      >
                        {configured}
                        {isStale ? ' \u26a0' : ''}
                      </span>
                    ) : (
                      <span className="text-slate-400">Not set</span>
                    )}
                  </Td>
                );
              })}

              <Td isNumeric className={CELL}>
                <PermissionsProvider
                  permission={AfriexAdminPermission.CurrencyUpdate}
                >
                  <Button
                    variant="unStyled"
                    onClick={() => showResolverUpdateForm(item)}
                  >
                    <span className="text-cyan-600 hover:underline text-sm">
                      Update
                    </span>
                  </Button>
                </PermissionsProvider>
              </Td>
            </Tr>
          );
        })}
      </TBody>
    </Table>
  );
};

export default ResolverTable;
